import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { trackAction } from '@/hooks/useActivityTracker';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Users, Repeat, EyeOff, UserMinus, Clock, TrendingUp, ShoppingBag,
  ChevronLeft, ChevronRight, ArrowUpDown, Search, Filter, X, HeartPulse
} from 'lucide-react';

const API = '/api';

const SEGMENTS = [
  { key: 'repeat_buyers', label: 'Repeat Buyers', icon: Repeat, color: 'bg-emerald-500', lightBg: 'bg-emerald-50', text: 'text-emerald-700', desc: '3+ wins' },
  { key: 'window_shoppers', label: 'Window Shoppers', icon: EyeOff, color: 'bg-amber-500', lightBg: 'bg-amber-50', text: 'text-amber-700', desc: '5+ leads, 0 wins' },
  { key: 'churned', label: 'Churned', icon: UserMinus, color: 'bg-red-500', lightBg: 'bg-red-50', text: 'text-red-700', desc: 'Won but inactive 6m+' },
  { key: 'dormant', label: 'Dormant', icon: Clock, color: 'bg-slate-500', lightBg: 'bg-slate-50', text: 'text-slate-700', desc: 'Open leads, no activity 30d+' },
  { key: 'rising', label: 'Rising', icon: TrendingUp, color: 'bg-blue-500', lightBg: 'bg-blue-50', text: 'text-blue-700', desc: '2+ enquiries in 90d' },
  { key: 'single_purchase', label: 'Single Purchase', icon: ShoppingBag, color: 'bg-violet-500', lightBg: 'bg-violet-50', text: 'text-violet-700', desc: '1 win, gone quiet' },
];

const TIME_RANGES = [
  { value: 'all', label: 'All Time' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '6m', label: 'Last 6 Months' },
  { value: '1y', label: 'Last 1 Year' },
];

function KpiCard({ title, value, subtitle, icon: Icon, loading }) {
  if (loading) return <Skeleton className="h-28 rounded-xl" />;
  return (
    <Card data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`} className="border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          <div className="p-2 bg-slate-100 rounded-lg">
            <Icon className="h-5 w-5 text-slate-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SegmentPill({ seg, count, active, onClick }) {
  const Icon = seg.icon;
  return (
    <button
      data-testid={`segment-pill-${seg.key}`}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all text-sm font-medium whitespace-nowrap ${
        active
          ? `${seg.lightBg} ${seg.text} border-current shadow-sm`
          : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
      }`}
    >
      <Icon className="h-4 w-4" />
      <span>{seg.label}</span>
      <Badge variant="secondary" className={`ml-1 h-5 px-1.5 text-[10px] font-bold ${active ? seg.lightBg : ''}`}>
        {count ?? '-'}
      </Badge>
    </button>
  );
}

function HealthBadge({ score }) {
  if (score == null) return null;
  const color = score >= 70 ? 'bg-emerald-100 text-emerald-700' : score >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600';
  return <Badge className={`${color} text-xs font-bold tabular-nums`}>{score}</Badge>;
}

export default function CustomerIntelligence() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [segment, setSegment] = useState('repeat_buyers');
  const [timeRange, setTimeRange] = useState('all');
  const [dealer, setDealer] = useState('');
  const [dealers, setDealers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [sortBy, setSortBy] = useState('won_count');
  const [sortDir, setSortDir] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const limit = 20;

  // Fetch overview
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setLoading(true);
        const params = {};
        if (dealer) params.dealer = dealer;
        const { data } = await axios.get(`${API}/customers/overview`, { params, withCredentials: true });
        setOverview(data);
      } catch (err) {
        console.error('Failed to fetch overview', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();
  }, [dealer]);

  // Fetch dealers list
  useEffect(() => {
    axios.get(`${API}/customers/dealers`, { withCredentials: true })
      .then(res => setDealers(res.data.dealers || []))
      .catch(() => {});
  }, []);

  // Fetch segment data
  const fetchSegment = useCallback(async () => {
    try {
      setTableLoading(true);
      const params = { segment, time_range: timeRange, page, limit, sort_by: sortBy, sort_dir: sortDir };
      if (dealer) params.dealer = dealer;
      const { data } = await axios.get(`${API}/customers/segments`, { params, withCredentials: true });
      setCustomers(data.customers || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch segment', err);
    } finally {
      setTableLoading(false);
    }
  }, [segment, timeRange, dealer, page, sortBy, sortDir]);

  useEffect(() => { fetchSegment(); }, [fetchSegment]);

  // Reset page when segment/filters change
  useEffect(() => { setPage(1); }, [segment, timeRange, dealer]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const SortHeader = ({ field, children }) => (
    <TableHead
      className="cursor-pointer select-none hover:text-slate-700 text-xs"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === field && <ArrowUpDown className="h-3 w-3" />}
      </div>
    </TableHead>
  );

  const activeSegment = SEGMENTS.find(s => s.key === segment);

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="customer-intelligence-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Customer Intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">Analyze customer behavior patterns and identify opportunities</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
            className={showFilters ? 'bg-slate-100' : ''}
          >
            <Filter className="h-4 w-4 mr-1" />
            Filters
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="border-slate-200" data-testid="filters-panel">
          <CardContent className="p-4 flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">Time Range</label>
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-[140px] h-9" data-testid="time-range-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_RANGES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-500">Dealer</label>
              <Select value={dealer || 'all'} onValueChange={v => setDealer(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[200px] h-9" data-testid="dealer-filter">
                  <SelectValue placeholder="All Dealers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Dealers</SelectItem>
                  {dealers.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(dealer || timeRange !== 'all') && (
              <Button variant="ghost" size="sm" onClick={() => { setDealer(''); setTimeRange('all'); }} className="text-slate-400">
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard title="Total Customers" value={overview?.total_unique_customers?.toLocaleString() || '-'} icon={Users} loading={loading} subtitle="Unique phone numbers" />
        <KpiCard title="Repeat Buyer Rate" value={`${overview?.repeat_buyer_rate || 0}%`} icon={Repeat} loading={loading} subtitle="3+ wins" />
        <KpiCard title="Avg Lifetime" value={`${overview?.avg_lifetime_days || 0}d`} icon={TrendingUp} loading={loading} subtitle="First to last enquiry" />
        <KpiCard title="Churn Rate" value={`${overview?.churn_rate || 0}%`} icon={UserMinus} loading={loading} subtitle="Won but inactive 6m+" />
        <KpiCard title="Avg Health Score" value={overview?.avg_health_score || '-'} icon={HeartPulse} loading={loading} subtitle="Repeat buyers only" />
      </div>

      {/* Segment Pills */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-testid="segment-pills">
        {SEGMENTS.map(seg => (
          <SegmentPill
            key={seg.key}
            seg={seg}
            count={overview?.segment_counts?.[seg.key]}
            active={segment === seg.key}
            onClick={() => { trackAction('segment_switch', 'customers', null, { segment: seg.key }); setSegment(seg.key); }}
          />
        ))}
      </div>

      {/* Segment Table */}
      <Card className="border-slate-200" data-testid="customer-table-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {activeSegment && <activeSegment.icon className={`h-5 w-5 ${activeSegment.text}`} />}
              <CardTitle className="text-lg">{activeSegment?.label || 'Customers'}</CardTitle>
              <Badge variant="outline" className="ml-2">{total} customers</Badge>
            </div>
          </div>
          <p className="text-xs text-slate-400">{activeSegment?.desc}</p>
        </CardHeader>
        <CardContent className="p-0">
          {tableLoading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : customers.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No customers in this segment</p>
              <p className="text-sm mt-1">Try changing filters or time range</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead className="text-xs w-[50px]">#</TableHead>
                      <SortHeader field="name">Customer</SortHeader>
                      <TableHead className="text-xs">Phone</TableHead>
                      <TableHead className="text-xs">Dealer(s)</TableHead>
                      <SortHeader field="total_leads">Total Leads</SortHeader>
                      <SortHeader field="won_count">Won</SortHeader>
                      <SortHeader field="lost_count">Lost</SortHeader>
                      <SortHeader field="open_count">Open</SortHeader>
                      <SortHeader field="conversion_rate">Conv. %</SortHeader>
                      {segment === 'repeat_buyers' && <SortHeader field="health_score">Health</SortHeader>}
                      <SortHeader field="last_enquiry">Last Enquiry</SortHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((c, idx) => (
                      <TableRow
                        key={c.phone}
                        data-testid={`customer-row-${c.phone}`}
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => navigate(`/customers/${encodeURIComponent(c.phone)}`)}
                      >
                        <TableCell className="text-xs text-slate-400 font-mono">{(page - 1) * limit + idx + 1}</TableCell>
                        <TableCell>
                          <div className="font-medium text-sm text-slate-900 max-w-[200px] truncate">{c.name || '-'}</div>
                          {c.names?.length > 1 && (
                            <span className="text-[10px] text-slate-400">+{c.names.length - 1} alias</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-mono text-slate-600">{c.phone}</TableCell>
                        <TableCell>
                          <div className="text-sm text-slate-600 max-w-[180px] truncate">{c.dealer || '-'}</div>
                          {c.dealers?.length > 1 && (
                            <span className="text-[10px] text-slate-400">+{c.dealers.length - 1} more</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm font-semibold text-slate-700">{c.total_leads}</TableCell>
                        <TableCell><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{c.won_count}</Badge></TableCell>
                        <TableCell><Badge className="bg-red-100 text-red-600 hover:bg-red-100">{c.lost_count}</Badge></TableCell>
                        <TableCell><Badge className="bg-blue-100 text-blue-600 hover:bg-blue-100">{c.open_count}</Badge></TableCell>
                        <TableCell className="text-sm font-medium">{c.conversion_rate?.toFixed(1)}%</TableCell>
                        {segment === 'repeat_buyers' && <TableCell><HealthBadge score={c.health_score} /></TableCell>}
                        <TableCell className="text-xs text-slate-500">{c.last_enquiry || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100" data-testid="pagination">
                <p className="text-xs text-slate-400">
                  Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs font-medium text-slate-600 px-2">{page}/{totalPages}</span>
                  <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
